
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import FbxButton from '@/components/fbx-button.vue';
import VirtualRedTagHeader from '@/components/VirtualRedTag/VirtualRedTagHeader.vue';
import VirtualRedTagTrailerGraphic from '@/components/VirtualRedTag/VirtualRedTagTrailerGraphic.vue';
import LoadingModal from '@/components/LoadingModal.vue';
import trailerByNumber from '@/shared/mixins/trailerByNumber';
import checkVirtualRedTagEligibility from '@/shared/mixins/checkVirtualRedTagEligibility';
import { ActionTypes } from '@/store/actionTypes';
import { VIRTUAL_RED_TAG_ORIGIN } from '@/types/WorkflowTypes';
import { VirtualRedTagTrailerGraphicState } from '@/types/VirtualRedTagTrailerGraphicState';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import { isNullOrUndefined } from '@/shared/helpers';

export default defineComponent({
    name: 'VirtualRedTagConfirmation',
    components: {
        FbxButton,
        LoadingModal,
        VirtualRedTagHeader,
        VirtualRedTagTrailerGraphic,
    },
    mixins: [trailerByNumber, checkVirtualRedTagEligibility],
    props: {
        verified: {
            type: Boolean,
            default: false,
        },
    },
    data: (): {
        eligibilityResolved: boolean;
        hardEligibilityConfirmed: boolean;
    } => {
        return { eligibilityResolved: false, hardEligibilityConfirmed: false };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            selectedTrailer: 'selectedTrailer',
            selectedTrailerSelectionMethod: 'getTrailerSelectionMethod',
        }),
        // Two sources, either of which blocks. The backend sees reservations the client
        // never loaded, but it is a network call: if it fails we still have to warn on
        // the reservation already attached to the trailer we hold.
        isBlocked(): boolean {
            return (
                this.hasConflictingLocalReservation ||
                this.virtualRedTagReservedForAnotherDriver
            );
        },
        hasConflictingLocalReservation(): boolean {
            const reservation = this.trailer?.reservation;
            return (
                !!reservation?.isActive &&
                !isNullOrUndefined(reservation.driverId) &&
                reservation.driverId.toUpperCase() !==
                    this.driver?.id?.toUpperCase()
            );
        },
        isVerified(): boolean {
            return this.verified && !this.isBlocked;
        },
        graphicState(): VirtualRedTagTrailerGraphicState {
            if (this.isBlocked) {
                return VirtualRedTagTrailerGraphicState.blocked;
            }

            if (this.isVerified) {
                return VirtualRedTagTrailerGraphicState.success;
            }

            return VirtualRedTagTrailerGraphicState.default;
        },
        // The plain "Would you like to Red Tag this trailer?" question, as opposed to
        // the warning and thank-you titles.
        isPrompt(): boolean {
            return (
                !this.virtualRedTagEligibilityCheckFailed &&
                !this.isBlocked &&
                !this.isVerified
            );
        },
        title(): string {
            // Distinct from isBlocked: that flag also reads a failed call as a
            // reservation conflict (see virtualRedTagReservedForAnotherDriver), which
            // is the right call for warning copy but the wrong one for a title that
            // otherwise claims a specific, unconfirmed fact about the trailer.
            if (this.virtualRedTagEligibilityCheckFailed) {
                return "We couldn't confirm this trailer is eligible for Red Tag";
            }

            if (this.isBlocked) {
                return 'Trailer is attached to another driver in the system';
            }

            if (this.isVerified) {
                return 'Thank you for verifying this trailer';
            }

            return 'Would you like to Red Tag this trailer?';
        },
        subtitle(): string | null {
            if (this.virtualRedTagEligibilityCheckFailed) {
                return 'Please check your connection and try again.';
            }

            if (this.isVerified) {
                return 'You may now continue to the Virtual Red Tag assessment.';
            }

            return null;
        },
    },
    async created(): Promise<void> {
        await this.fetchTrailerByNumber();

        if (!this.trailer) {
            this.eligibilityResolved = true;
            return;
        }

        // Asked again here rather than carried over from the entry screen, so the
        // reservation state survives a refresh or a shared link.
        const canRedTag = await this.checkVirtualRedTagEligibility({
            trailerNumber: this.trailer.trailerNumber,
            trailerCompany: this.trailer.company,
            driverCompany: this.driverCompany,
            driverId: this.driver?.id,
        });

        // The trailer can become ineligible while the driver is verifying it, so a
        // rejection here has to be acted on rather than dropped. A failed call is
        // different: we never got an answer, so we can't declare the trailer
        // ineligible, and redirecting to VIN/plate verification would just bounce the
        // driver back here in a loop once they pass it. Resolve as usual instead; the
        // reservation computed already reads a failed call as "reserved" and shows its
        // existing warning banner rather than silently waving the driver through.
        if (!canRedTag && !this.virtualRedTagEligibilityCheckFailed) {
            this.$router.replace({
                name: 'VirtualRedTagUnavailable',
                params: { trailerNumber: this.trailer.trailerNumber },
                query: {
                    reason: this.virtualRedTagIneligibilityReason ?? undefined,
                    trailerCompany: this.trailer.company,
                },
            });
            return;
        }

        // A failed call reaches here with canRedTag false too (see the comment above),
        // so this is only true once the hard rules actually returned a real pass, never
        // just because the check didn't run to completion.
        this.hardEligibilityConfirmed = canRedTag;
        this.eligibilityResolved = true;
    },
    methods: {
        navigateToWelcome(): void {
            this.$router.push({ name: 'VirtualRedTagEntry' });
        },
        async continueToAssessment(): Promise<void> {
            if (
                !this.trailer ||
                !this.eligibilityResolved ||
                !this.hardEligibilityConfirmed
            ) {
                return;
            }

            const trailerSelectionMethod =
                this.selectedTrailer?.trailerNumber ===
                    this.trailer.trailerNumber &&
                this.selectedTrailerSelectionMethod
                    ? this.selectedTrailerSelectionMethod
                    : TrailerSelectionMethod.manual;

            await this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                trailer: this.trailer,
                trailerSelectionMethod,
            });

            this.$router.push({
                name: 'TrailerAssessment',
                params: { trailerNumber: this.trailer.trailerNumber },
                query: { origin: VIRTUAL_RED_TAG_ORIGIN },
            });
        },
    },
});
